/**
 * Feature Flags Context
 *
 * Fetches GET /api/flags/evaluate at app bootstrap (after auth) and exposes
 * the results as a `Record<string, boolean>` at app level.
 *
 * Usage
 * -----
 * // Gate a UI component:
 * const isBetaEnabled = useFlag("beta-dashboard");
 *
 * // Read loading state or all flags:
 * const { flags, isLoading, error } = useFeatureFlags();
 *
 * Rules
 * -----
 * - useFlag(key) returns `false` while loading, on error, or for unknown keys
 *   ("closed by default" — nothing renders before flags are resolved).
 * - The provider only mounts after login; it will never fire an
 *   unauthenticated request.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { evaluateAllFlags } from "../api/client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FeatureFlagMap = Record<string, boolean>;

interface FeatureFlagsContextValue {
  /** Evaluated flags for the current user, keyed by flag slug. */
  flags: FeatureFlagMap;
  /** True while the initial bootstrap fetch is in flight. */
  isLoading: boolean;
  /** Error message if the bootstrap fetch failed, otherwise null. */
  error: string | null;
  /** Manually re-fetch all flags (e.g. after an admin mutation). */
  refetch: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface FeatureFlagsProviderProps {
  children: React.ReactNode;
}

export function FeatureFlagsProvider({ children }: FeatureFlagsProviderProps) {
  const [flags, setFlags] = useState<FeatureFlagMap>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Guard against state updates on unmounted component.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchFlags = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const results = await evaluateAllFlags();
      if (!mountedRef.current) return;
      const map: FeatureFlagMap = {};
      for (const { key, enabled } of results) {
        map[key] = enabled;
      }
      setFlags(map);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : "Failed to load feature flags");
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  // Bootstrap: fetch once when the provider mounts (i.e. after login).
  useEffect(() => {
    void fetchFlags();
  }, [fetchFlags]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, isLoading, error, refetch: fetchFlags }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Returns the full feature flags context: flags map, loading state, error,
 * and a refetch function.
 *
 * Must be used inside a `<FeatureFlagsProvider>`.
 */
export function useFeatureFlags(): FeatureFlagsContextValue {
  const ctx = useContext(FeatureFlagsContext);
  if (ctx === null) {
    throw new Error("useFeatureFlags must be used inside <FeatureFlagsProvider>");
  }
  return ctx;
}

/**
 * Returns whether a specific feature flag is enabled for the current user.
 *
 * Returns `false` while loading, on error, or if the key does not exist
 * (closed/safe default).
 *
 * @example
 * const showBetaDashboard = useFlag("beta-dashboard");
 */
export function useFlag(key: string): boolean {
  const { flags } = useFeatureFlags();
  return flags[key] ?? false;
}
